// Painted boss parts: repaints the procedural boss part textures in place with the AI-painted
// cutout parts (src/art/foes). Every texture keeps its key, size and pivot - the painting is
// fitted to the footprint (opaque bounds) of the procedural part it replaces - so the boss
// rigs, hit areas and attack geometry are untouched. Damage states (phase 2 / 3) get battle
// damage drawn over the painting; glow overlays are rebuilt from the painting's own emissive
// pixels so they light up the painted lights, not the old ones.

import Phaser from 'phaser';
import { foeRig, type FoeRig } from '../../art/foes';
import { artRng, cracks, dents, scorch } from './artkit';
import { TITAN_TEX } from './art-titan';
import { WARDEN_TEX, type Dmg } from './art-warden';

type G = CanvasRenderingContext2D;
interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

const DMGS: readonly Dmg[] = [0, 1, 2];

function canvasOf(scene: Phaser.Scene, key: string): { tex: Phaser.Textures.CanvasTexture; g: G } | null {
  if (!scene.textures.exists(key)) return null;
  const tex = scene.textures.get(key);
  if (!(tex instanceof Phaser.Textures.CanvasTexture)) return null;
  return { tex, g: tex.getContext() };
}

/** Opaque bounds of a canvas (alpha > 24), or the whole canvas when empty. */
function opaqueBox(g: G): Box {
  const { width: w, height: h } = g.canvas;
  const d = g.getImageData(0, 0, w, h).data;
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      if (d[(y * w + x) * 4 + 3] <= 24) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  return x1 < 0 ? { x: 0, y: 0, w, h } : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** Trim the transparent margin of a part image (cut-parts pads every part). */
function trimmed(img: CanvasImageSource & { width: number; height: number }): { src: CanvasImageSource; box: Box } {
  const c = document.createElement('canvas');
  c.width = img.width;
  c.height = img.height;
  const g = c.getContext('2d')!;
  g.drawImage(img, 0, 0);
  return { src: c, box: opaqueBox(g) };
}

/**
 * Draw a painted part into a box: stretched to fill it (joints line up with the old part) when
 * the shapes are close, else fitted proportionally and centred (no distortion).
 */
function drawInto(g: G, img: CanvasImageSource & { width: number; height: number }, box: Box, mirror = false): void {
  const t = trimmed(img);
  const ra = t.box.w / t.box.h;
  const rb = box.w / box.h;
  if (Math.abs(Math.log(ra / rb)) > 0.2) {
    const k = Math.min(box.w / t.box.w, box.h / t.box.h);
    const w = t.box.w * k;
    const h = t.box.h * k;
    box = { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
  }
  g.save();
  g.imageSmoothingQuality = 'high';
  if (mirror) {
    g.translate(box.x * 2 + box.w, 0);
    g.scale(-1, 1);
  }
  g.drawImage(t.src, t.box.x, t.box.y, t.box.w, t.box.h, box.x, box.y, box.w, box.h);
  g.restore();
}

/** Battle damage over the painting (kept inside its silhouette). */
function wear(g: G, key: string, box: Box, d: Dmg, hot: number): void {
  if (d === 0) return;
  const rng = artRng(`painted:${key}`);
  const s = Math.max(box.w, box.h);
  g.save();
  g.globalCompositeOperation = 'source-atop';
  for (let i = 0; i < (d === 1 ? 3 : 6); i++) scorch(g, box.x + rng.float(0.1, 0.9) * box.w, box.y + rng.float(0.1, 0.9) * box.h, s * rng.float(0.08, 0.16), d === 1 ? 0.45 : 0.65);
  dents(g, rng, { x: box.x + box.w * 0.1, y: box.y + box.h * 0.1, w: box.w * 0.8, h: box.h * 0.8 }, d === 1 ? 5 : 10, 0x48566a);
  for (let i = 0; i < (d === 1 ? 2 : 4); i++) {
    cracks(g, rng, box.x + rng.float(0.2, 0.8) * box.w, box.y + rng.float(0.2, 0.8) * box.h, s * rng.float(0.12, 0.22), {
      branches: 2 + (i % 2),
      width: d === 1 ? 1.6 : 2.2,
      glow: d === 2 && i % 2 === 0 ? hot : undefined,
    });
  }
  g.restore();
}

/** Glow overlay from a painted canvas: its bright saturated (emissive) pixels, blurred. */
function emissive(src: G, dst: G, hue: 'red' | 'orange'): void {
  const { width: w, height: h } = src.canvas;
  const sd = src.getImageData(0, 0, w, h);
  const out = new ImageData(w, h);
  const p = sd.data;
  const o = out.data;
  for (let i = 0; i < p.length; i += 4) {
    const r = p[i];
    const gr = p[i + 1];
    const b = p[i + 2];
    const a = p[i + 3];
    if (a < 40) continue;
    const hot = hue === 'red' ? r > 170 && r > gr * 1.7 && r > b * 1.35 : r > 190 && gr > 70 && r > b * 1.9 && gr < r * 0.85;
    if (!hot) continue;
    o[i] = r;
    o[i + 1] = gr;
    o[i + 2] = b;
    o[i + 3] = Math.min(255, ((r - 120) / 135) * 255);
  }
  const tmp = document.createElement('canvas');
  tmp.width = w;
  tmp.height = h;
  tmp.getContext('2d')!.putImageData(out, 0, 0);
  dst.clearRect(0, 0, w, h);
  dst.save();
  dst.filter = 'blur(3px)';
  dst.drawImage(tmp, 0, 0);
  dst.filter = 'none';
  dst.globalAlpha = 0.8;
  dst.drawImage(tmp, 0, 0);
  dst.restore();
}

interface Slot {
  part: string;
  keys: readonly string[] | ((d: Dmg) => string);
  mirror?: boolean;
  /** Glow overlay rebuilt from this part's emissive pixels. */
  glow?: string;
}

function repaint(scene: Phaser.Scene, rig: FoeRig, slots: readonly Slot[], hue: 'red' | 'orange', hot: number): void {
  for (const s of slots) {
    const part = rig.parts[s.part];
    if (!part) continue;
    const keys = typeof s.keys === 'function' ? DMGS.map((d) => (s.keys as (d: Dmg) => string)(d)) : s.keys;
    let first: G | null = null;
    keys.forEach((key, i) => {
      const c = canvasOf(scene, key);
      if (!c) return;
      const box = opaqueBox(c.g);
      c.g.clearRect(0, 0, c.g.canvas.width, c.g.canvas.height);
      drawInto(c.g, part.img as HTMLImageElement, box, s.mirror);
      if (typeof s.keys === 'function') wear(c.g, key, box, DMGS[i], hot);
      c.tex.refresh();
      if (!first) first = c.g;
    });
    if (s.glow && first) {
      const gc = canvasOf(scene, s.glow);
      if (gc && gc.g.canvas.width === (first as G).canvas.width && gc.g.canvas.height === (first as G).canvas.height) {
        emissive(first, gc.g, hue);
        gc.tex.refresh();
      }
    }
  }
}

/** Repaint every boss whose painted parts are loaded (after buildBossArt). */
export function applyPaintedBossArt(scene: Phaser.Scene): void {
  const warden = foeRig(scene, 'warden');
  if (warden) {
    repaint(
      scene,
      warden,
      [
        { part: 'torso', keys: WARDEN_TEX.torso },
        { part: 'head', keys: WARDEN_TEX.head, glow: WARDEN_TEX.headGlow },
        { part: 'pelvis', keys: WARDEN_TEX.pelvis },
        { part: 'rack', keys: WARDEN_TEX.rack, glow: WARDEN_TEX.rackGlow },
        { part: 'thigh', keys: [WARDEN_TEX.thigh] },
        { part: 'shin', keys: [WARDEN_TEX.shin] },
        { part: 'foot', keys: [WARDEN_TEX.foot] },
        { part: 'antenna', keys: [WARDEN_TEX.antenna], glow: WARDEN_TEX.antennaGlow },
        { part: 'core', keys: [WARDEN_TEX.core] },
        { part: 'shutterTop', keys: [WARDEN_TEX.shutterTop] },
        { part: 'shutterBot', keys: [WARDEN_TEX.shutterBot] },
        { part: 'rackLid', keys: [WARDEN_TEX.rackLid] },
      ],
      'red',
      0xff3b5c,
    );
  }
  const titan = foeRig(scene, 'titan');
  if (titan) {
    repaint(
      scene,
      titan,
      [
        { part: 'hull', keys: TITAN_TEX.hull, glow: TITAN_TEX.hullGlow },
        { part: 'crucible', keys: TITAN_TEX.crucible, glow: TITAN_TEX.crucibleGlow },
        { part: 'hammer', keys: [TITAN_TEX.hammer], glow: TITAN_TEX.hammerGlow },
        { part: 'thruster', keys: [TITAN_TEX.thruster] },
        { part: 'boom', keys: [TITAN_TEX.boom] },
        { part: 'hook', keys: [TITAN_TEX.hook] },
        { part: 'nozzle', keys: [TITAN_TEX.nozzle] },
        { part: 'heart', keys: [TITAN_TEX.heart] },
        { part: 'lid', keys: [TITAN_TEX.lidL] },
        { part: 'lid', keys: [TITAN_TEX.lidR], mirror: true },
        { part: 'piston', keys: [TITAN_TEX.piston] },
        { part: 'chain', keys: [TITAN_TEX.chain] },
      ],
      'orange',
      0xff7a1a,
    );
  }
}

/** Singularity Heart core / plate textures from the painted parts (null = procedural). */
export function paintHeartTextures(scene: Phaser.Scene): boolean {
  const rig = foeRig(scene, 'heart');
  if (!rig) return false;
  // Sized from the painting's own proportions (core ~190 px tall, plates ~150 px); the plate
  // image bows toward the left, so it is mirrored to face its convex side outward.
  for (const [part, key, height, mirror] of [
    ['core', 'boss_heart_core', 190, false],
    ['plate', 'boss_heart_plate', 150, true],
  ] as const) {
    if (scene.textures.exists(key)) continue;
    const img = rig.parts[part].img as HTMLImageElement;
    const t = trimmed(img);
    const w = Math.max(8, Math.round((height * t.box.w) / t.box.h));
    const tex = scene.textures.createCanvas(key, w, height);
    if (!tex) return false;
    drawInto(tex.getContext(), img, { x: 0, y: 0, w, h: height }, mirror);
    tex.refresh();
  }
  return true;
}
